import type { DryRunHistory, DryRunHistoryItem } from "~/features/dataImportProjects/types";

export class DataImportPipelineJobHistoryService {
  private readonly STORAGE_KEY_PREFIX = "dewey/dryRunJobHistory";
  private readonly MAX_ITEMS = 50;
  private readonly TTL_DAYS = 30;
  private readonly STORAGE_EVENT_NAME = "dryRunHistoryUpdated";

  async saveHistory(jobType: string, item: DryRunHistoryItem): Promise<void> {
    try {
      const history = await this.getHistoryFromStorage(jobType);

      // Add new item at the beginning
      history.unshift(item);

      // Apply constraints
      const constrainedHistory = await this.enforceMaxItems(history);

      // Save to localStorage
      await this.saveHistoryToStorage(jobType, constrainedHistory, true);
    } catch (error) {
      if (error instanceof Error && error.message === "QuotaExceededError") {
        throw new Error("Storage quota exceeded");
      }
      throw error;
    }
  }

  async getHistory(jobType: string): Promise<DryRunHistory> {
    const history = await this.getHistoryFromStorage(jobType);
    const cleanedHistory = await this.cleanupExpiredItems(history);

    // Save cleaned history back to storage without dispatching event
    if (cleanedHistory.length !== history.length) {
      await this.saveHistoryToStorage(jobType, cleanedHistory, false);
    }

    return cleanedHistory;
  }

  async getHistoryByJobRunId(jobType: string, jobRunId: string): Promise<DryRunHistoryItem | null> {
    const history = await this.getHistoryFromStorage(jobType);
    return history.find((item) => item.jobRunId === jobRunId) || null;
  }

  async updateHistoryItem(jobType: string, jobRunId: string, updatedItem: DryRunHistoryItem): Promise<void> {
    const history = await this.getHistoryFromStorage(jobType);
    const index = history.findIndex((item) => item.jobRunId === jobRunId);

    if (index !== -1) {
      history[index] = updatedItem;
      await this.saveHistoryToStorage(jobType, history, true);
    }
  }

  async listHistoryItemsByJobType(jobType: string): Promise<DryRunHistory> {
    return this.getHistory(jobType);
  }

  private getStorageKey(jobType: string): string {
    return [this.STORAGE_KEY_PREFIX, jobType].join("/");
  }

  private async cleanupExpiredItems(history: DryRunHistory): Promise<DryRunHistory> {
    const now = new Date();
    const ttlMs = this.TTL_DAYS * 24 * 60 * 60 * 1000;

    return history.filter((item) => {
      const executedAt = new Date(item.executedAt);
      const age = now.getTime() - executedAt.getTime();
      return age <= ttlMs;
    });
  }

  private async enforceMaxItems(history: DryRunHistory): Promise<DryRunHistory> {
    // Sort by executedAt (newest first) and take only MAX_ITEMS
    return history
      .sort((a, b) => new Date(b.executedAt).getTime() - new Date(a.executedAt).getTime())
      .slice(0, this.MAX_ITEMS);
  }

  private async getHistoryFromStorage(jobType: string): Promise<DryRunHistory> {
    return new Promise((resolve) => {
      // Simulate async operation
      setTimeout(() => {
        const stored = localStorage.getItem(this.getStorageKey(jobType));
        if (!stored) {
          resolve([]);
          return;
        }

        try {
          const history = JSON.parse(stored) as DryRunHistory;
          resolve(history);
        } catch {
          resolve([]);
        }
      }, 0);
    });
  }

  private async saveHistoryToStorage(
    jobType: string,
    history: DryRunHistory,
    shouldDispatchEvent = false,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      // Simulate async operation
      setTimeout(() => {
        try {
          localStorage.setItem(this.getStorageKey(jobType), JSON.stringify(history));
          // Dispatch custom event to notify listeners only when explicitly requested
          if (shouldDispatchEvent) {
            window.dispatchEvent(
              new CustomEvent(this.STORAGE_EVENT_NAME, {
                detail: { jobType },
              }),
            );
          }
          resolve();
        } catch (error) {
          reject(error);
        }
      }, 0);
    });
  }
}
